import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from '@ucl/fpds/themeProvider';
import { Button } from '@ucl/fpds/button';
import { TextInput } from '@ucl/fpds/textInput';
import { Checkbox } from '@ucl/fpds/checkbox';
import { Table } from '@ucl/fpds/table';
import { Pagination } from '@ucl/fpds/pagination';
import { ModalWindow } from '@ucl/fpds/modalWindow';
import { Drawer } from '@ucl/fpds/drawer';
import { Snackbar } from '@ucl/fpds/snackbar';
import { Icon } from '@ucl/fpds/icons';
import { Link } from '@ucl/fpds/link';
import '../../styles.css';
function escapeGraphqlString(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function buildServiceHistoryQuery(filters) {
    const args = [`limit: ${filters?.limit ?? 100}`, `offset: ${filters?.offset ?? 0}`];
    if (filters?.vin) {
        args.unshift(`vin: "${escapeGraphqlString(filters.vin)}"`);
    }
    if (filters?.repairOrderId) {
        args.unshift(`repairOrderId: "${escapeGraphqlString(filters.repairOrderId)}"`);
    }
    if (filters?.ffmCompanyId) {
        args.unshift(`ffmCompanyId: "${escapeGraphqlString(filters.ffmCompanyId)}"`);
    }
    return `
    query ServiceHistoryRecords {
      getServiceHistory(${args.join(', ')}) {
        totalCount
        hasNextPage
        items {
          vin
          repairOrderId
          dataSource
          url
          repairOrderCloseDate
          totalAmount
          odometerReading
          engineHours
          serviceDescription
          ffmCompanyId
          partsTotalAmount
          laborTotalAmount
          miscellaneousTotalAmount
          taxTotalAmount
        }
      }
    }
  `;
}
const DEFAULT_GRAPHQL_ENDPOINT = '/fis/sm/graphql';
function formatCellValue(value, fallback = '-') {
    if (value === null || value === undefined)
        return fallback;
    const asString = String(value).trim();
    return asString.length > 0 ? asString : fallback;
}
function formatDataSourceLabel(value) {
    const source = formatCellValue(value).toLowerCase();
    if (source === 'dcds')
        return 'Ford Dealership';
    if (source === 'fleetio')
        return 'Ford Fleet Management';
    if (source === 'telematics')
        return 'Manually Added';
    return formatCellValue(value);
}
export function mapGraphqlFleetResponse(payload) {
    const serviceHistoryData = payload.data?.getServiceHistory;
    const list = serviceHistoryData?.items ?? payload.data?.serviceHistory ?? payload.data?.serviceHistoryRecords;
    if (!Array.isArray(list))
        return [];
    return list.map((item) => {
        const row = (item ?? {});
        const vehicleName = '';
        return {
            vehicle: formatCellValue(row.vin),
            vehicleName,
            serviceHistoryNo: formatCellValue(row.repairOrderId).replace(/^\[|\]$/g, '').trim(),
            dataSource: formatDataSourceLabel(row.dataSource),
            url: formatCellValue(row.url),
            date: formatCellValue(row.repairOrderCloseDate),
            odometerMiles: formatCellValue(row.odometerReading),
            engineHours: formatCellValue(row.engineHours),
            serviceCenter: formatCellValue(row.ffmCompanyId, '-'),
            issuesAddressed: formatCellValue(row.serviceDescription),
            serviceCategory: formatCellValue(row.serviceDescription),
            laborCost: formatCellValue(row.laborTotalAmount),
            partsCost: formatCellValue(row.partsTotalAmount),
            totalCost: formatCellValue(row.totalAmount),
        };
    });
}
export function buildFleetCsv(rows) {
    const headers = ['Vehicle', 'Service History No', 'Date', 'Odometer(mi)', 'Engine Hours', 'Service Center', 'Issues Addressed', 'Service Categories', 'Total Cost'];
    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const contentRows = rows.map((row) => [row.vehicle, row.serviceHistoryNo, row.date, row.odometerMiles, row.engineHours, row.serviceCenter, row.issuesAddressed, row.serviceCategory, row.totalCost].map(escape).join(','));
    return [headers.join(','), ...contentRows].join('\n');
}
function getGraphqlEndpoint() {
    return process.env.BFF_GRAPHQL_ENDPOINT || DEFAULT_GRAPHQL_ENDPOINT;
}
function getSearchFilters(searchTerm) {
    const normalizedTerm = searchTerm?.trim();
    if (!normalizedTerm) {
        return undefined;
    }
    const looksLikeVin = /^[A-HJ-NPR-Z0-9]{17}$/i.test(normalizedTerm);
    if (looksLikeVin) {
        return { vin: normalizedTerm.toUpperCase() };
    }
    return { repairOrderId: normalizedTerm };
}
async function fetchFleetServiceHistoryRows(searchTerm) {
    const filters = getSearchFilters(searchTerm);
    const response = await fetch(getGraphqlEndpoint(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: buildServiceHistoryQuery(filters) }),
    });
    if (!response.ok) {
        throw new Error(`Failed to fetch service history: ${response.status}`);
    }
    const payload = (await response.json());
    if (Array.isArray(payload.errors) && payload.errors.length > 0) {
        throw new Error(payload.errors[0]?.message || 'GraphQL query failed');
    }
    return mapGraphqlFleetResponse(payload);
}
function getPageNumbers(currentPage, totalPages) {
    if (totalPages <= 7)
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 4)
        return [1, 2, 3, 4, 5, '...', totalPages];
    if (currentPage >= totalPages - 3)
        return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
}
function getSortValue(row, column) {
    switch (column) {
        case 'odometerMiles': {
            const v = Number(row.odometerMiles);
            return Number.isFinite(v) ? v : Number.NEGATIVE_INFINITY;
        }
        case 'engineHours': {
            const v = Number(row.engineHours);
            return Number.isFinite(v) ? v : Number.NEGATIVE_INFINITY;
        }
        case 'totalCost': {
            const v = Number(row.totalCost);
            return Number.isFinite(v) ? v : Number.NEGATIVE_INFINITY;
        }
        default:
            return row[column].toLowerCase();
    }
}
function isFleetManagementSource(dataSource) {
    const normalizedSource = dataSource.trim().toLowerCase();
    return normalizedSource === 'fleetio' || normalizedSource === 'ford fleet management';
}
const COLUMNS = [
    { key: 'vehicle', label: 'Vehicle', sortable: true },
    { key: 'serviceHistoryNo', label: 'Service History No', sortable: true },
    { key: 'date', label: 'Date', sortable: true },
    { key: 'odometerMiles', label: 'Odometer(mi)', sortable: true },
    { key: 'engineHours', label: 'Engine Hours', sortable: true },
    { key: 'serviceCenter', label: 'Service Center', sortable: true },
    { key: 'issuesAddressed', label: 'Issues Addressed', sortable: true },
    { key: 'serviceCategory', label: 'Service Categories', sortable: false },
    { key: 'totalCost', label: 'Total Cost', sortable: false },
];
function ServiceHistoryFleetView() {
    const [rows, setRows] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedYears, setSelectedYears] = useState(new Set());
    const [drawerRow, setDrawerRow] = useState(null);
    const [showFleetManagementAlert, setShowFleetManagementAlert] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);
    const [showDownloadError, setShowDownloadError] = useState(false);
    const searchRequestSequenceRef = useRef(0);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [categoryListScrollTop, setCategoryListScrollTop] = useState(0);
    const categoryDropdownRef = useRef(null);
    const categoryListRef = useRef(null);
    useEffect(() => {
        const requestId = searchRequestSequenceRef.current + 1;
        searchRequestSequenceRef.current = requestId;
        const normalizedSearchTerm = searchTerm.trim();
        const timerId = window.setTimeout(() => {
            void (async () => {
                setIsLoading(true);
                setErrorMessage('');
                try {
                    const fetchedRows = await fetchFleetServiceHistoryRows(normalizedSearchTerm);
                    if (requestId !== searchRequestSequenceRef.current)
                        return;
                    setRows(fetchedRows);
                    setCurrentPage(1);
                }
                catch {
                    if (requestId !== searchRequestSequenceRef.current)
                        return;
                    setRows([]);
                    setErrorMessage('Unable to load service history right now.');
                }
                finally {
                    if (requestId === searchRequestSequenceRef.current)
                        setIsLoading(false);
                }
            })();
        }, normalizedSearchTerm ? 350 : 0);
        return () => {
            window.clearTimeout(timerId);
        };
    }, [searchTerm]);
    const categories = useMemo(() => {
        return Array.from(new Set(rows.map((r) => r.serviceCategory)));
    }, [rows]);
    const visibleCategories = useMemo(() => {
        const query = categorySearchTerm.trim().toLowerCase();
        if (!query) {
            return categories;
        }
        return categories.filter((category) => category.toLowerCase().includes(query));
    }, [categories, categorySearchTerm]);
    useEffect(() => {
        setSelectedCategories((prev) => {
            const next = new Set(Array.from(prev).filter((category) => categories.includes(category)));
            if (next.size === prev.size) {
                return prev;
            }
            return next;
        });
    }, [categories]);
    useEffect(() => {
        if (!isCategoryDropdownOpen) {
            return;
        }
        const handleOutsideClick = (event) => {
            if (categoryDropdownRef.current?.contains(event.target)) {
                return;
            }
            setIsCategoryDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isCategoryDropdownOpen]);
    useEffect(() => {
        if (!isCategoryDropdownOpen || !categoryListRef.current) {
            return;
        }
        categoryListRef.current.scrollTop = categoryListScrollTop;
    }, [isCategoryDropdownOpen, categoryListScrollTop]);
    const availableYears = useMemo(() => {
        const years = new Set();
        rows.forEach((row) => {
            if (!row.date || row.date === '-')
                return;
            const isoMatch = row.date.match(/^(\d{4})-/);
            if (isoMatch) {
                years.add(isoMatch[1]);
                return;
            }
            const mdyMatch = row.date.match(/\/(\d{4})$/);
            if (mdyMatch) {
                years.add(mdyMatch[1]);
            }
        });
        return Array.from(years).sort((a, b) => Number(b) - Number(a));
    }, [rows]);
    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            if (selectedCategories.size > 0 && !selectedCategories.has(row.serviceCategory)) {
                return false;
            }
            return true;
        });
    }, [rows, selectedCategories]);
    const sortedRows = useMemo(() => {
        if (!sortConfig)
            return filteredRows;
        const { column, direction } = sortConfig;
        return [...filteredRows].sort((a, b) => {
            const va = getSortValue(a, column);
            const vb = getSortValue(b, column);
            if (va < vb)
                return direction === 'asc' ? -1 : 1;
            if (va > vb)
                return direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortConfig]);
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategories, searchTerm]);
    const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
    const paginatedRows = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, currentPage, rowsPerPage]);
    const getYearFromDate = (date) => {
        if (!date || date === '-')
            return null;
        const isoMatch = date.match(/^(\d{4})-/);
        if (isoMatch)
            return isoMatch[1];
        const mdyMatch = date.match(/\/(\d{4})$/);
        if (mdyMatch)
            return mdyMatch[1];
        return null;
    };
    const handleDownloadCsv = () => {
        setSelectedYears(new Set());
        setIsExportModalOpen(true);
    };
    const handleModalDownload = () => {
        setIsDownloading(true);
        try {
            const rowsToExport = selectedYears.size === 0
                ? []
                : sortedRows.filter((row) => {
                    const year = getYearFromDate(row.date);
                    return year !== null && selectedYears.has(year);
                });
            const blob = new Blob([buildFleetCsv(rowsToExport)], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'service-history-fleet.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setIsDownloading(false);
            setIsExportModalOpen(false);
            setShowDownloadSuccess(true);
            window.setTimeout(() => setShowDownloadSuccess(false), 5000);
        }
        catch {
            setIsDownloading(false);
            setIsExportModalOpen(false);
            setShowDownloadError(true);
            window.setTimeout(() => setShowDownloadError(false), 5000);
        }
    };
    const toggleYear = (year) => {
        setSelectedYears((prev) => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            }
            else {
                next.add(year);
            }
            return next;
        });
    };
    const allYearsSelected = availableYears.length > 0 && selectedYears.size === availableYears.length;
    const toggleSelectAll = () => setSelectedYears(allYearsSelected ? new Set() : new Set(availableYears));
    return (_jsxs(ThemeProvider, { productTeamName: "rpst-sm-unified-service-ui", children: [_jsxs("section", { "aria-label": "service history fleet view", className: "shvev-root", children: [_jsxs("div", { className: "shvev-controls", children: [showFleetManagementAlert && (_jsxs("div", { className: "shvev-fleet-alert", role: "status", "aria-live": "polite", children: [_jsx("span", { className: "shvev-fleet-alert-icon", "aria-hidden": "true", children: _jsxs("svg", { viewBox: "0 0 24 24", width: "18", height: "18", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M9 4H5.5C4.67 4 4 4.67 4 5.5V18.5C4 19.33 4.67 20 5.5 20H18.5C19.33 20 20 19.33 20 18.5V15", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M11 13L20 4", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M14 4H20V10", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }), _jsx("span", { children: "For vehicles subscribed only to Ford Fleet Management, vehicle details will open in Ford Fleet Management" })] })), _jsxs("div", { className: "shvev-controls-top", children: [_jsx("div", { className: "shvev-control-group shvev-control-search", children: _jsx(TextInput, { id: "fleet-service-history-search", "aria-label": "Type vehicle name, vin or service no", placeholder: "Type vehicle name, vin or service no", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), startAdornmentProp: {
                                                startAdornment: (_jsxs("svg", { "aria-hidden": "true", xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })] })),
                                            } }) }), _jsxs(Button, { variant: "secondary", className: "shvev-download-btn", onClick: handleDownloadCsv, children: [_jsx(Icon, { icon: "download", size: "s" }), "Download Service History"] })] }), _jsx("div", { className: "shvev-control-group shvev-control-category", ref: categoryDropdownRef, children: _jsxs("div", { className: "shvev-category-dropdown", children: [_jsxs(Button, { variant: "secondary", id: "fleet-service-category-filter", "aria-label": "Filter by Service Categories", "aria-haspopup": "listbox", "aria-expanded": isCategoryDropdownOpen, className: "shvev-category-trigger", onClick: () => setIsCategoryDropdownOpen((prev) => !prev), children: [_jsx("span", { className: "shvev-category-label", children: "Service Categories" }), selectedCategories.size > 0 && (_jsx("span", { className: "shvev-category-count", "aria-label": `${selectedCategories.size} selected`, children: selectedCategories.size })), _jsx("span", { className: `shvev-category-chevron ${isCategoryDropdownOpen ? 'open' : ''}`, "aria-hidden": "true", children: _jsx(Icon, { icon: "caretDown", size: "m" }) })] }), isCategoryDropdownOpen && (_jsxs("div", { className: "shvev-category-menu", children: [_jsx("div", { className: "shvev-category-search-wrap", children: _jsx(TextInput, { "aria-label": "Search service categories", placeholder: "Search...", value: categorySearchTerm, onChange: (event) => setCategorySearchTerm(event.target.value) }) }), _jsx("div", { className: "shvev-category-options", role: "listbox", "aria-label": "Service category options", ref: categoryListRef, onScroll: (event) => setCategoryListScrollTop(event.currentTarget.scrollTop), children: visibleCategories.length === 0 ? (_jsx("p", { className: "shvev-category-no-results", children: "No results found" })) : (visibleCategories.map((category) => (_jsx(Checkbox, { label: category, checkboxValue: selectedCategories.has(category) ? 2 : 0, onChange: (_e, val) => {
                                                            setSelectedCategories((prev) => {
                                                                const next = new Set(prev);
                                                                if (val === 2) {
                                                                    next.add(category);
                                                                }
                                                                else {
                                                                    next.delete(category);
                                                                }
                                                                return next;
                                                            });
                                                        } }, category)))) })] }))] }) })] }), isLoading && _jsx("p", { role: "status", children: "Loading service history..." }), !isLoading && errorMessage && _jsx("p", { role: "alert", children: errorMessage }), !isLoading && !errorMessage && (_jsxs("div", { className: "shvev-table-shell", children: [_jsxs(Table, { className: "shvev-table", children: [_jsx(Table.Head, { children: COLUMNS.map((column) => {
                                            const isActive = sortConfig?.column === column.key;
                                            return (_jsx(Table.Header, { scope: "col", "aria-sort": column.sortable && isActive ? (sortConfig?.direction === 'asc' ? 'ascending' : 'descending') : undefined, children: column.sortable ? (_jsxs(Button, { variant: "tertiary", className: "shvev-th-button", onClick: () => {
                                                        const nextDir = isActive && sortConfig?.direction === 'asc' ? 'desc' : 'asc';
                                                        setSortConfig({ column: column.key, direction: nextDir });
                                                        setCurrentPage(1);
                                                    }, "aria-label": `Sort by ${column.label}${isActive ? ` (${sortConfig?.direction === 'asc' ? 'ascending' : 'descending'})` : ''}`, children: [column.label, isActive ? (_jsx("span", { className: "shvev-sort-icon--active", "aria-hidden": "true", children: sortConfig?.direction === 'asc' ? '▲' : '▼' })) : (_jsxs("span", { className: "shvev-sort-icon", "aria-hidden": "true", children: [_jsx("span", { className: "shvev-sort-tri", children: "\u25B2" }), _jsx("span", { className: "shvev-sort-tri", children: "\u25BC" })] }))] })) : column.label }, column.key));
                                        }) }), _jsx(Table.Body, { children: paginatedRows.map((row, index) => (_jsxs(Table.Row, { id: `fleet-row-${index}`, children: [_jsx(Table.Cell, { children: _jsxs("div", { className: "shvev-vehicle-cell", children: [row.vehicleName && (_jsx("span", { className: "shvev-vehicle-name", children: row.vehicleName })), _jsxs("div", { className: "shvev-vehicle-vin-row", children: [row.url !== '-' ? (_jsx(Link, { to: row.url, target: "_blank", rel: "noreferrer", className: "shvev-vehicle-vin", size: "small", children: row.vehicle })) : (_jsx("span", { className: "shvev-vehicle-vin", children: row.vehicle })), isFleetManagementSource(row.dataSource) && (_jsx(Button, { variant: "tertiary", className: "shvev-redirect-icon-btn", onClick: () => setShowFleetManagementAlert(true), "aria-label": "Show Ford Fleet Management notice", children: _jsxs("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M9 4H5.5C4.67 4 4 4.67 4 5.5V18.5C4 19.33 4.67 20 5.5 20H18.5C19.33 20 20 19.33 20 18.5V15", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M11 13L20 4", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M14 4H20V10", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] }) }))] })] }) }), _jsxs(Table.Cell, { children: [_jsx(Button, { variant: "tertiary", className: "shvev-service-link-btn", onClick: () => setDrawerRow(row), "aria-label": `View details for ${row.serviceHistoryNo}`, children: row.serviceHistoryNo }), _jsx("span", { className: "shvev-source-subtext", children: row.dataSource })] }), _jsx(Table.Cell, { children: row.date }), _jsx(Table.Cell, { children: row.odometerMiles }), _jsx(Table.Cell, { children: row.engineHours }), _jsx(Table.Cell, { children: row.serviceCenter }), _jsx(Table.Cell, { children: row.issuesAddressed }), _jsx(Table.Cell, { children: row.serviceCategory }), _jsx(Table.Cell, { children: row.totalCost })] }, `${row.vehicle}-${row.serviceHistoryNo}`))) })] }), sortedRows.length === 0 && _jsx("p", { className: "shvev-empty", children: "No service history records found." }), (showDownloadSuccess || showDownloadError) && (_jsx("div", { className: "shvev-snackbar-wrapper", children: _jsx(Snackbar, { type: showDownloadSuccess ? 'primary' : 'error', message: showDownloadSuccess ? 'Service History Downloaded Successfully.' : "Couldn't download Service History.", dismissible: true, onClose: () => { setShowDownloadSuccess(false); setShowDownloadError(false); } }) })), sortedRows.length > 0 && (_jsxs("div", { className: "shvev-pagination", children: [_jsxs("div", { className: "shvev-pagination-left", children: [_jsx(Pagination, { totalItems: sortedRows.length, itemsPerPage: rowsPerPage, page: currentPage, showPerPageOption: true, showTotalItems: false, hidePageListButtons: true, contentAlignment: "left", perPageOptions: [10, 20, 30, 40, 50, 100], initialItemsPerPage: rowsPerPage, onItemsPerPageChange: (val) => {
                                                    setRowsPerPage(val);
                                                    setCurrentPage(1);
                                                } }), _jsxs("span", { className: "shvev-pagination-total", children: [sortedRows.length, " Total"] })] }), _jsx(Pagination, { totalItems: sortedRows.length, itemsPerPage: rowsPerPage, page: currentPage, onPageChange: (page) => setCurrentPage(page), showPerPageOption: false, showTotalItems: false, contentAlignment: "right" })] }))] }))] }), _jsxs(ModalWindow, { isOpen: isExportModalOpen, onClose: () => setIsExportModalOpen(false), variant: "medium", children: [_jsx(ModalWindow.Header, { hasClose: true, onClose: () => setIsExportModalOpen(false), children: "Download Service History" }), _jsxs(ModalWindow.Body, { children: [_jsx("p", { className: "shvev-modal-subtitle", children: "Select the calender years you want to download Service History for" }), _jsx("p", { className: "shvev-modal-section-label", children: "Year" }), _jsx(Checkbox, { label: "All", checkboxValue: allYearsSelected ? 2 : 0, onChange: () => toggleSelectAll() }), _jsx("div", { className: "shvev-modal-years-grid", children: (() => {
                                    const half = Math.ceil(availableYears.length / 2);
                                    const leftCol = availableYears.slice(0, half);
                                    const rightCol = availableYears.slice(half);
                                    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "shvev-modal-years-col", children: leftCol.map((year) => (_jsx(Checkbox, { label: year, checkboxValue: selectedYears.has(year) ? 2 : 0, onChange: () => toggleYear(year) }, year))) }), _jsx("div", { className: "shvev-modal-years-col", children: rightCol.map((year) => (_jsx(Checkbox, { label: year, checkboxValue: selectedYears.has(year) ? 2 : 0, onChange: () => toggleYear(year) }, year))) })] }));
                                })() })] }), _jsxs(ModalWindow.Footer, { placement: "right", children: [_jsx(Button, { variant: "secondary", onClick: () => setIsExportModalOpen(false), children: "Cancel" }), _jsx(Button, { variant: "primary", disabled: selectedYears.size === 0 || isDownloading, className: isDownloading ? 'shvev-download-btn-loading' : '', onClick: handleModalDownload, children: isDownloading ? 'Downloading Service History...' : 'Download' })] })] }), _jsxs(Drawer, { isOpen: drawerRow !== null, onClose: () => setDrawerRow(null), placement: "right", size: "medium", children: [_jsx(Drawer.Header, { titleProp: { title: drawerRow?.serviceHistoryNo ?? '', headingLevel: 'h2' }, onClose: () => setDrawerRow(null) }), _jsx(Drawer.Content, { children: drawerRow !== null && (_jsxs("div", { className: "shvev-drawer-body", children: [_jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Vehicle Name" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.vehicle })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Date" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.date })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Odometer" }), _jsxs("span", { className: "shvev-drawer-value", children: [drawerRow.odometerMiles, " mi"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Engine Hours" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.engineHours })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Center" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.serviceCenter })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Service Categories" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.serviceCategory })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Issues Addressed" }), _jsx("span", { className: "shvev-drawer-value", children: drawerRow.issuesAddressed })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Labor" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", drawerRow.laborCost, " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Parts" }), _jsxs("span", { className: "shvev-drawer-value", children: ["Cost: ", drawerRow.partsCost, " USD"] })] }), _jsx("div", { className: "shvev-drawer-divider" }), _jsxs("div", { className: "shvev-drawer-row", children: [_jsx("span", { className: "shvev-drawer-label", children: "Total Cost" }), _jsxs("span", { className: "shvev-drawer-value", children: [drawerRow.totalCost, " USD"] })] })] })) }), _jsx(Drawer.Footer, { children: _jsx(Button, { variant: "secondary", onClick: () => setDrawerRow(null), children: "Close" }) })] })] }));
}
export default ServiceHistoryFleetView;
